import React, { useContext, useEffect, useState } from 'react';
import { EpubReader } from '@comp/EpubReader';
import styled from '@emotion/styled';
import useFetch from '@hooks/useFetch';
import { localStorageAvailable } from '@utils/localStorage';
import { EpubReaderProps } from '@comp/EpubReader/types';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { useRouter } from 'next/router';
import { evtNameCategoryMap } from '@constants/index';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import { emptyObject } from '@constants/empty';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { Spacings } from '@styles/index';
import useChildNoteInfo from '@api/hooks/notes/useChildNoteInfo';
import {
  EPUB_NOTES,
  getNotesEndpoint,
  NOTES_VIEWED,
  NOTES_PAGE_NAVIGATION_CLICKED,
  themes
} from './utils';
import { EpubContext } from './Context/EpubContext';

interface FullScreenProp {
  fullscreen: boolean;
}

interface IEpubContainerProps {
  resize: boolean;
  onImageClick: EpubReaderProps['handleImageClick'];
  setCurrentNotesUID: React.Dispatch<any>;
}

const ReaderContainer = styled.div<FullScreenProp>`
  font-size: 16px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.6s ease;
  ${(props) => !props.fullscreen && '0 0 5px rgba(0,0,0,.3);'}
`;

const ReaderWrapper = styled.div`
  position: relative;
  grid-template-columns: repeat(18, 32px);
`;

const EpubContainer: React.FC<IEpubContainerProps> = ({
  resize,
  onImageClick,
  setCurrentNotesUID
}) => {
  const {
    fontSize,
    searchObj,
    selectedConcept,
    onSearchChange,
    onChangeSearchResults,
    onChangeSearchPosition,
    onDisableNavigation
  } = useContext(EpubContext);
  const { data: epubDetails = [], isLoading } = useFetch(
    getNotesEndpoint(selectedConcept?.uid)
  );
  const {
    query: { goalUID }
  } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const { noteId } = epubDetails[0] || emptyObject;
  const { searchTerm, currentSearchPosition } = searchObj;
  const { childNoteInfo, mutate: refetchNotes } = useChildNoteInfo(noteId, {
    revalidateOnMount: true
  });

  const { notesUrl: epubUrl } = childNoteInfo;
  const [rendition, setRendition] = useState(false);
  const [location, setLocation] = useState(
    localStorageAvailable() && localStorage.getItem('epub-position')
      ? JSON.parse(localStorage.getItem('epub-position'))
      : { [selectedConcept?.uid]: 0 }
  );

  const onLocationChanged = (locationInBook) => {
    const locationObj = { ...location, [selectedConcept.uid]: locationInBook };
    setLocation(locationObj);
    if (localStorageAvailable())
      localStorage.setItem('epub-position', JSON.stringify(locationObj));
  };

  const getRendition = (rendition) => {
    rendition.themes.fontSize('100%');
    setRendition(rendition);
  };

  const onNavigation = ({
    direction,
    currentChapter,
    pageNoInChapter,
    isStartOfBook,
    isEndOfBook
  }) => {
    segment.track(
      NOTES_PAGE_NAVIGATION_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_title: selectedConcept.name,
        notes_uid: String(noteId),
        page_navigation: direction,
        current_chapter: currentChapter,
        page_number_in_chapter: pageNoInChapter,
        is_end: isEndOfBook,
        is_start: isStartOfBook
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
  };

  useEffect(() => {
    onSearchChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epubUrl]);

  useEffect(() => {
    refetchNotes();
  }, [selectedConcept?.uid, refetchNotes]);

  useFirstRenderSkip(() => {
    if (!isLoading && epubDetails) {
      let notesSegmentData = {};
      if (localStorageAvailable()) {
        notesSegmentData = JSON.parse(localStorage.getItem('notesSegmentData'));
      }
      segment.track(
        NOTES_VIEWED,
        {
          ...goalSegmentData,
          ...notesSegmentData,
          last_primary_source: EPUB_NOTES,
          last_primary_source_section: EPUB_NOTES,
          notes_title: selectedConcept?.name,
          notes_uid: String(noteId),
          note_type: 'EPub'
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        SYLLABUS_CLICKED_KEYS
      );
    }
    setCurrentNotesUID(String(noteId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, epubDetails, goalSegmentData]);

  if (!selectedConcept || !selectedConcept?.uid) return null;

  return (
    <ReaderWrapper>
      <ReaderContainer fullscreen={false}>
        <EpubReader
          showToc={false}
          url={epubUrl}
          location={location[selectedConcept.uid]}
          resize={resize}
          searchTerm={searchTerm}
          currentSearchPosition={currentSearchPosition}
          onChangeSearchResults={onChangeSearchResults}
          locationChanged={onLocationChanged}
          getRendition={getRendition}
          handleImageClick={onImageClick}
          onNavigation={onNavigation}
          onChangeSearchPosition={onChangeSearchPosition}
          onDisableNavigation={onDisableNavigation}
          theme={{
            fontSize,
            color: isDarkModeOn ? themes.DARK : themes.LIGHT
          }}
          epubOptions={{
            flow: 'paginated',
            spread: 'none',
            manager: 'continuous',
            resizeOnOrientationChange: true,
            allowScriptedContent: true
          }}
        />
      </ReaderContainer>
    </ReaderWrapper>
  );
};

export default EpubContainer;
